import dayjs from "dayjs";
import toast from "react-hot-toast";
import { memo, useMemo } from "react";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { useSearchParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Button, Dialog, DialogContent, DialogTitle, Tooltip } from "@mui/material";

import Loader from "../../../Components/Loader/Loader";
import DataTable from "../../../Components/DataGrid/DataGrid";
import { scheduledReportsApi } from "../../api/scheduled-reports/scheduled-report.service";
import ScheduledReportsSubscriptionsForm, { SubscriptionSubmitValues } from "./ScheduledReportsSubscriptionsForm";

function getModalProps(modalMode: string | null): { fullWidth?: boolean; maxWidth: "sm" | "md" } {
  if (modalMode === "delete") {
    return {
      maxWidth: "sm",
    };
  }

  return {
    maxWidth: "md",
    fullWidth: true,
  };
}

function getModalTitle(modalMode: string | null) {
  if (modalMode === "create") {
    return "Add New Subscription";
  }

  if (modalMode === "edit") {
    return "Edit Subscription";
  }

  return "Delete Subscription";
}

const ActionsColumns = (params: GridRenderCellParams) => {
  const [_, setSearchParams] = useSearchParams();

  function openEditModal() {
    setSearchParams(
      {
        modal: "edit",
        id: params.row.id,
      },
      { replace: true },
    );
  }

  function openDeleteModal() {
    setSearchParams(
      {
        modal: "delete",
        id: params.row.id,
      },
      { replace: true },
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Tooltip placement="bottom" title="Edit" disableInteractive onClick={openEditModal}>
        <EditIcon className="cursor-pointer" />
      </Tooltip>
      <Tooltip placement="bottom" title="Edit" disableInteractive onClick={openDeleteModal}>
        <DeleteIcon className="cursor-pointer" />
      </Tooltip>
    </div>
  );
};

const columns: GridColDef[] = [
  {
    field: "actions",
    headerName: "",
    type: "actions",
    renderCell: ActionsColumns,
    hideable: false,
    disableExport: true,
  },
  {
    field: "enable",
    headerName: "Enabled",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
    valueFormatter: (row) => {
      return row.value ? "Enabled" : "Disabled";
    },
  },
  {
    field: "user",
    headerName: "User",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
    valueGetter: (row) => {
      const user = row.value;
      return `${user.first_name} ${user.last_name}`;
    },
  },
  {
    field: "frequency",
    headerName: "Frequency",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
  },
  {
    field: "sent_on",
    headerName: "Report Time",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
    valueFormatter: (row) => {
      return dayjs(row.value).minute(0).format("h:mm A");
    },
  },
  {
    field: "template",
    headerName: "Template",
    sortable: true,
    minWidth: 110,
    hideable: true,
    flex: 1,
    valueGetter: (row) => {
      const template = row.value;
      return template.name;
    },
  },
];

const {
  useDeleteSubscriptionMutation,
  useEditSubscriptionMutation,
  useGetSubscriptionsQuery,
  usePostSubscriptionMutation,
} = scheduledReportsApi;

const ScheduledReportsSubscriptions = () => {
  const { data = [], isFetching } = useGetSubscriptionsQuery();
  const [createSubscription] = usePostSubscriptionMutation();
  const [deleteSubscription] = useDeleteSubscriptionMutation();
  const [editSubscription] = useEditSubscriptionMutation();

  const [searchParams, setSearchParams] = useSearchParams();

  // Determine modal state from search params
  const modalMode = searchParams.get("modal");
  const id = searchParams.get("id") ?? "";

  const initial_values = useMemo(() => {
    const current_time = dayjs().minute(0).millisecond(0);
    const DEFAULT_INITIAL_VALUE = { send_on_hour: current_time };
    if (modalMode !== "edit" && !id) return DEFAULT_INITIAL_VALUE;

    const _subscription = data.find((subscription) => subscription.id === id);
    console.log("_subscription", _subscription);

    if (!_subscription) return DEFAULT_INITIAL_VALUE;

    return {
      enable: { id: _subscription.enable, label: _subscription.enable ? "Enabled" : "Disabled" },
      frequency: _subscription.frequency,
      report_template_id: _subscription.template,
      send_on_hour: dayjs(_subscription.sent_on).minute(0),
      user: _subscription.user,
    };
  }, [modalMode, id, data]);

  // Open create modal
  const openCreateModal = () => {
    setSearchParams({ modal: "create" }, { replace: true });
  };

  const handleCloseModal = () => {
    setSearchParams({}, { replace: true }); // Removes all search params
  };

  async function handleFormSubmit(values: SubscriptionSubmitValues) {
    console.log("values", values);
    const { enable, frequency, report_template_id, send_on_hour, user } = values;

    const req_obj = {
      send_on_hour: dayjs(send_on_hour).hour(),
      frequency,
      user_id: user.userid ?? user?.id ?? "",
      enable: enable.id,
      report_template_id: report_template_id.id,
    };

    if (!req_obj.user_id) {
      console.error("SCHEDULE_REPORT_SUB_ERROR COULD_NOT_FIND_USER_ID", req_obj);
      return;
    }

    const toast_id = toast.loading("Submitting...");
    try {
      if (modalMode === "create") {
        await createSubscription({ body: req_obj }).unwrap();
      } else {
        await editSubscription({ params: { id }, body: req_obj }).unwrap();
      }
      toast.success("Success", { id: toast_id });
      handleCloseModal();
    } catch (error) {
      console.error("SCHEDULED_REPORT_TEMPLATE_ERROR", error);
      toast.error("Unable to Submit Request", { id: toast_id });
    }
  }

  async function handleDeleteItem() {
    const toast_id = toast.loading("Deleting...");
    try {
      await deleteSubscription({ params: { id } }).unwrap();
      toast.success("Success", { id: toast_id });
      handleCloseModal();
    } catch (error) {
      console.error("SCHEDULED_REPORT_TEMPLATE_DELETE_ERROR", error);
      toast.error("Unable to Submit Request", { id: toast_id });
    }
  }

  const FormComponent = memo(() => {
    if (modalMode === "edit" && isFetching)
      return (
        <div style={{ height: "10rem" }}>
          <Loader />
        </div>
      );

    if (modalMode === "delete") {
      return (
        <div>
          <div>Are you sure you want to delete this subscription?</div>
          <div className="flex justify-end items-center gap-2 mt-2">
            <Button variant="outlined" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={() => handleDeleteItem()}>
              Delete
            </Button>
          </div>
        </div>
      );
    }
    return <ScheduledReportsSubscriptionsForm defaultValues={initial_values} handleSubmit={handleFormSubmit} />;
  });

  const modal_props = useMemo(() => getModalProps(modalMode), [modalMode]);
  const modal_title = getModalTitle(modalMode);

  return (
    <div style={{ height: "80vh"}}>
      <Button variant="contained" onClick={openCreateModal} style={{ marginBottom: "0.5rem" }}>
        Create
      </Button>
      {(modalMode === "create" || modalMode === "edit" || modalMode === "delete") && (
        <Dialog open={true} onClose={handleCloseModal} {...modal_props}>
          <DialogTitle className="flex items-center justify-between">
            {modal_title}
            <CloseIcon className="assets-info-popup-close-icon pointer" onClick={handleCloseModal} />
          </DialogTitle>
          <DialogContent>
            <FormComponent />
          </DialogContent>
        </Dialog>
      )}
      <DataTable
        tableInfo={{
          id: "audit-log-table",
          label: "Audit Log",
          requireAuthSign: false,
          timestamp: { startTs: null, endTs: null },
        }}
        loading={isFetching}
        rows={data}
        columns={columns}
        showFilterButton={false}
      />
    </div>
  );
};

export default ScheduledReportsSubscriptions;
